import 'react-notifications/lib/notifications.css'
import React, { Component } from 'react'
import { Buffer } from 'buffer'
import { NotificationContainer, NotificationManager } from 'react-notifications'
import { FaBell, FaCheck, FaEdit, FaExternalLinkAlt, FaImage, FaPlus, FaTimes } from 'react-icons/fa'
import AdminLayout from '../../../components/AdminLayout'
import GameService from '../../../services/game_services'

const emptyForm = {
    title: '',
    description: '',
    link: '',
    picture: null,
    picturePreview: null,
    start_date: '',
    end_date: '',
}

class AdminNews extends Component {
    constructor(props) {
        super(props)
        this.state = {
            news: [],
            includeDeactivated: false,
            processing: false,
            form: { ...emptyForm },
            editForm: { ...emptyForm },
            editNewsUuid: null,
            showUpdateModal: false,
        }
    }

    componentDidMount = async () => {
        await this.loadNews()
    }

    loadNews = async () => {
        try {
            const news = await GameService.getNews(this.state.includeDeactivated)
            console.log('Loaded news:', news)
            this.setState({ news: Array.isArray(news) ? news : [] })
        } catch (error) {
            NotificationManager.error('Unable to load news')
        }
    }

    updateForm = (key, value, edit = false) => {
        const formKey = edit ? 'editForm' : 'form'
        this.setState({ [formKey]: { ...this.state[formKey], [key]: value } })
    }

    onPictureHandler = (event, edit = false) => {
        const file = event.target.files[0]
        if (!file) return

        const extension = file.name.split('.').pop().toLowerCase()
        if (!['jpeg', 'jpg', 'png', 'webp'].includes(extension)) {
            NotificationManager.error('Invalid image format')
            return
        }
        if (file.size > 5000000) {
            NotificationManager.error('File exceeding 5MB')
            return
        }

        const reader = new FileReader()
        reader.onloadend = () => {
            const base64data = Buffer.from(reader.result).toString('base64')
            this.updateForm('picture', `data:${file.type};base64,${base64data}`, edit)
        }
        reader.readAsArrayBuffer(file)
    }

    buildPayload = (form, isUpdate = false) => {
        const payload = {
            title: form.title.trim(),
            description: form.description.trim(),
            link: form.link.trim(),
        }
        if (form.picture) payload.picture = form.picture
        if (form.start_date) payload.start_date = new Date(form.start_date).toISOString()
        if (form.end_date) payload.end_date = new Date(form.end_date).toISOString()

        if (isUpdate) return payload
        return payload
    }

    validateForm = (form) => {
        if (!form.title.trim()) {
            NotificationManager.error('Title is required')
            return false
        }
        if (form.start_date && form.end_date && new Date(form.end_date) <= new Date(form.start_date)) {
            NotificationManager.error('End date must be after start date')
            return false
        }
        return true
    }

    createNews = async () => {
        if (!this.validateForm(this.state.form) || !window.confirm('Do you confirm news creation?')) return

        this.setState({ processing: true })
        try {
            await GameService.createNews(this.buildPayload(this.state.form))
            NotificationManager.success('News created')
            this.setState({ form: { ...emptyForm } })
            await this.loadNews()
        } catch (error) {
            NotificationManager.error(error.response?.data?.message || 'Unable to create news')
        } finally {
            this.setState({ processing: false })
        }
    }

    toDateTimeLocal = (date) => {
        if (!date) return ''
        const value = new Date(date)
        value.setMinutes(value.getMinutes() - value.getTimezoneOffset())
        return value.toISOString().slice(0, 16)
    }

    openUpdateModal = (item) => {
        this.setState({
            showUpdateModal: true,
            editNewsUuid: item.news_uuid,
            editForm: {
                title: item.title || '',
                description: item.description || '',
                link: item.link_url || item.link || '',
                picture: null,
                picturePreview: item.picture || item.img || null,
                start_date: this.toDateTimeLocal(item.start_date),
                end_date: this.toDateTimeLocal(item.end_date),
            },
        })
    }

    closeUpdateModal = () => {
        this.setState({
            showUpdateModal: false,
            editNewsUuid: null,
            editForm: { ...emptyForm },
        })
    }

    updateNews = async () => {
        if (!this.validateForm(this.state.editForm) || !window.confirm('Do you confirm news update?')) return

        this.setState({ processing: true })
        try {
            await GameService.updateNews(this.state.editNewsUuid, this.buildPayload(this.state.editForm, true))
            NotificationManager.success('News updated')
            this.closeUpdateModal()
            await this.loadNews()
        } catch (error) {
            NotificationManager.error(error.response?.data?.message || 'Unable to update news')
        } finally {
            this.setState({ processing: false })
        }
    }

    setNewsActivation = async (item, activate) => {
        const action = activate ? 'reactivate' : 'deactivate'
        if (!window.confirm(`Do you confirm news ${action}?`)) return

        try {
            if (activate) await GameService.activateNews(item.news_uuid)
            else await GameService.deactivateNews(item.news_uuid)
            NotificationManager.success(`News ${activate ? 'reactivated' : 'deactivated'}`)
            await this.loadNews()
        } catch (error) {
            NotificationManager.error(error.response?.data?.message || `Unable to ${action} news`)
        }
    }

    renderFormFields = (form, edit = false) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                    type="text"
                    value={form.title}
                    onChange={(event) => this.updateForm('title', event.target.value, edit)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
            </div>
            <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                    value={form.description}
                    onChange={(event) => this.updateForm('description', event.target.value, edit)}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-y"
                />
            </div>
            <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Link</label>
                <input
                    type="url"
                    placeholder="https://"
                    value={form.link}
                    onChange={(event) => this.updateForm('link', event.target.value, edit)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start date (optional)</label>
                <input
                    type="datetime-local"
                    value={form.start_date}
                    onChange={(event) => this.updateForm('start_date', event.target.value, edit)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End date (optional)</label>
                <input
                    type="datetime-local"
                    value={form.end_date}
                    onChange={(event) => this.updateForm('end_date', event.target.value, edit)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
            </div>
            <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <FaImage /> {edit ? 'New image (optional)' : 'Image (optional)'} - max 5MB
                </label>
                <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(event) => this.onPictureHandler(event, edit)}
                    className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200"
                />
                {(form.picture || form.picturePreview) && (
                    <div className="mt-3">
                        <p className="text-xs font-medium text-gray-500 mb-2">
                            {form.picture ? 'New image preview' : 'Current image'}
                        </p>
                        <img
                            src={form.picture || form.picturePreview}
                            alt="News preview"
                            className="w-full max-w-md max-h-72 object-contain bg-gray-50 rounded-lg border border-gray-200"
                        />
                    </div>
                )}
            </div>
        </div>
    )

    render() {
        return (
            <>
                <AdminLayout title="News Management" breadcrumbs={[{ label: 'News' }]}>
                    <div className="space-y-6">
                        <section className="bg-blue-50 p-6 border border-blue-200 rounded-lg">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <FaPlus className="text-blue-600" /> Create News
                            </h2>
                            {this.renderFormFields(this.state.form)}
                            <button
                                onClick={this.createNews}
                                disabled={this.state.processing}
                                className="mt-5 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center gap-2"
                            >
                                <FaPlus /> {this.state.processing ? 'Processing...' : 'Create News'}
                            </button>
                        </section>

                        <div className="flex flex-wrap justify-between items-center gap-3">
                            <h2 className="text-lg font-semibold text-gray-900">Published News</h2>
                            <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={this.state.includeDeactivated}
                                    onChange={(event) => this.setState({ includeDeactivated: event.target.checked }, this.loadNews)}
                                    className="w-4 h-4 accent-blue-600"
                                />
                                Include deactivated news
                            </label>
                        </div>

                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                            <table className="w-full min-w-[900px]">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">News</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Period</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Link</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {this.state.news.map((item) => (
                                        <tr key={item.news_uuid} className="hover:bg-gray-50">
                                            <td className="px-4 py-4">
                                                <div className="flex items-start gap-3 max-w-md">
                                                    {(item.picture || item.img) ? (
                                                        <a href={item.picture || item.img} target="_blank" rel="noreferrer" className="shrink-0">
                                                            <img
                                                                src={item.picture || item.img}
                                                                alt={item.title || 'News'}
                                                                className="w-24 h-24 object-contain bg-gray-50 rounded-lg border border-gray-200"
                                                            />
                                                        </a>
                                                    ) : (
                                                        <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center shrink-0"><FaBell className="text-gray-400" /></div>
                                                    )}
                                                    <div className="min-w-0">
                                                        <p className="font-medium text-gray-900">{item.title}</p>
                                                        <p className="mt-1 text-sm text-gray-600 line-clamp-3">{item.description || '-'}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">
                                                <p>{item.start_date ? new Date(item.start_date).toLocaleString() : 'Immediate'}</p>
                                                <p className="mt-1">{item.end_date ? new Date(item.end_date).toLocaleString() : 'Default duration'}</p>
                                            </td>
                                            <td className="px-4 py-4 text-sm">
                                                {(item.link_url || item.link) ? (
                                                    <a href={item.link_url || item.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800">
                                                        Open <FaExternalLinkAlt />
                                                    </a>
                                                ) : '-'}
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${item.deactivated ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                                    {item.deactivated ? 'Deactivated' : 'Active'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex justify-center gap-2">
                                                    <button onClick={() => this.openUpdateModal(item)} className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200" title="Edit news">
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => this.setNewsActivation(item, item.deactivated)}
                                                        className={`p-2 rounded-lg ${item.deactivated ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                                        title={item.deactivated ? 'Reactivate news' : 'Deactivate news'}
                                                    >
                                                        {item.deactivated ? <FaCheck /> : <FaTimes />}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {!this.state.news.length && (
                                        <tr><td colSpan="5" className="px-4 py-10 text-center text-gray-500">No news found</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </AdminLayout>

                {this.state.showUpdateModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
                                <h2 className="text-xl font-semibold flex items-center gap-2"><FaEdit /> Update News</h2>
                                <button onClick={this.closeUpdateModal} className="p-2 hover:bg-blue-700 rounded-lg" title="Close"><FaTimes /></button>
                            </div>
                            <div className="p-6">
                                {this.renderFormFields(this.state.editForm, true)}
                                <div className="flex justify-end gap-3 mt-6">
                                    <button onClick={this.closeUpdateModal} className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                                    <button onClick={this.updateNews} disabled={this.state.processing} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                                        <FaCheck /> {this.state.processing ? 'Processing...' : 'Update News'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <NotificationContainer />
            </>
        )
    }
}

export default AdminNews